import React, { Fragment } from "react";
import { Card, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { createUseStyles } from "react-jss";
import { OpenAnswer } from "../components/OpenAnswer";
import { Header } from "../components/Header";
import { ButtonText } from "../components/questions/ButtonText";
import { ButtonNumber } from "../components/questions/ButtonNumber";
import { Stars } from "../components/questions/Stars";
import { Emoji } from "../components/questions/Emoji";
import { ButtonCheck } from "../components/questions/ButtonCheck";
import surveys from "../questions.json";
import { useSurveyState } from "hooks/useSurveyState";
import { motion } from "framer-motion";
import { SectionHeader } from "components/SectionHeader";
import { QuestionsRawType, QuestionType, SurveyType } from "types";

const STARS_RANGE = 5;
const TEXT_ONLY_STYLE = "TextOnly";

const questionComponents = {
  ButtonCheck,
  ButtonNumber,
  ButtonText,
  Emoji,
  Stars,
};

export const Survey = () => {
  const classes = useStyles();

  const {
    handleButtonRatingClick,
    handleSubmit,
    formValues,
    setFormValues,
    currentSection,
    sectionsNumber,
    currentPercentage,
    nextPercentage,
    isSendingSurvey,
    handleBackButton,
    questionsRaw: questions,
  } = useSurveyState(surveys as SurveyType[]);

  function checkCondition(question: QuestionType) {
    if (!question.condition || question.condition.length === 0) return true;

    return question.condition.every(
      ({
        questionId,
        expectedAnswer,
      }: {
        questionId: string;
        expectedAnswer: number;
      }) => {
        return (
          formValues.find((q) => q.questionId === questionId)?.answer ===
          expectedAnswer
        );
      }
    );
  }

  return (
    <div className="mx-0 mx-sm-auto my-5 overflow-hidden">
      <Card className={classes.noShadow}>
        <Card.Body>
          <Header currentSection={currentSection} />

          {isSendingSurvey && (
            <div className={classes.spinnerOuterContainer}>
              <div className={classes.spinnerInnerContainer}>
                <Spinner />
              </div>
            </div>
          )}

          {questions.map((section, id) => {
            let progressiveQuestionNumber = 0;
            return (
              <div key={`${section.sectionNumber}-${id}`} className="mt-2">
                {currentSection === section.sectionNumber && (
                  <Fragment>
                    <SectionHeader
                      section={section}
                      currentPercentage={currentPercentage}
                      nextPercentage={nextPercentage}
                    />

                    <motion.div
                      key={section.sectionNumber}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -30 }}
                      transition={{
                        duration: 0.5,
                        delay: 0.1,
                        ease: [0.42, 0, 0.58, 1],
                      }}
                    >
                      <Form className="px-1">
                        {section.questions.map((question, idx) => {
                          const DynamicQuestion =
                            questionComponents[
                              question.style as keyof typeof questionComponents
                            ];

                          if (question.style !== TEXT_ONLY_STYLE) {
                            progressiveQuestionNumber++;
                          }
                          return (
                            checkCondition(question) && (
                              <Row className="text-start mt-5" key={idx}>
                                <Col xs={12} className="mb-2">
                                  <p className="fw-bold lh-sm">
                                    {question.style !== TEXT_ONLY_STYLE && (
                                      <span
                                        className={classes.dilsRed}
                                      >{`${progressiveQuestionNumber}. `}</span>
                                    )}
                                    {question.text}
                                  </p>
                                </Col>

                                {question.style !== TEXT_ONLY_STYLE && (
                                  <DynamicQuestion
                                    section={section as QuestionsRawType}
                                    question={question as QuestionType}
                                    formValues={formValues}
                                    handleButtonRatingClick={
                                      handleButtonRatingClick
                                    }
                                    range={STARS_RANGE}
                                  />
                                )}

                                {(
                                  question as QuestionType
                                ).openAnswersTrigger.includes(
                                  formValues.find(
                                    (q) => q.questionId === question.id
                                  )?.answer!
                                ) && (
                                  <OpenAnswer
                                    section={section as QuestionsRawType}
                                    formValues={formValues}
                                    setFormValues={setFormValues}
                                    question={question}
                                    questions={section.questions}
                                  />
                                )}
                              </Row>
                            )
                          );
                        })}
                      </Form>
                      <Card.Footer className="pt-2 px-1 mt-5 border-0">
                        <Row>
                          {currentSection > 1 && (
                            <Col xs={2}>
                              <Button
                                className={`${classes.backButton} px-1`}
                                onClick={handleBackButton}
                              >
                                <i className="las la-angle-left" />
                              </Button>
                            </Col>
                          )}
                          <Col xs={currentSection > 1 ? 10 : 12}>
                            <Button
                              onClick={handleSubmit}
                              className={classes.submitButton}
                            >
                              {currentSection < sectionsNumber
                                ? "Avanti"
                                : "Completa Sondaggio"}

                              <i className="las la-angle-right" />
                            </Button>
                          </Col>
                        </Row>
                      </Card.Footer>
                    </motion.div>
                  </Fragment>
                )}
              </div>
            );
          })}
        </Card.Body>
      </Card>
    </div>
  );
};

const useStyles = createUseStyles({
  dilsRed: {
    color: "#e73838",
  },
  noShadow: {
    boxShadow: "none!important",
  },
  spinnerOuterContainer: {
    height: "100%",
    width: "100%",
    position: "fixed",
    zIndex: 1,
    top: 0,
    left: 0,
    backgroundColor: "#FFF",
    opacity: ".7",
  },
  spinnerInnerContainer: {
    height: "100vh",
    display: "flex",
    placeContent: "center",
    flexWrap: "wrap",
  },
  submitButton: {
    width: "100%",
    backgroundColor: "#e73838!important",
    fontSize: 15,
    boxShadow: "none!important",
    fontWeight: 700,
  },
  backButton: {
    width: "100%",
    backgroundColor: "rgb(237, 239, 248)!important",
    color: "#404254!important",
    fontSize: 15,
    boxShadow: "none!important",
    fontWeight: 700,
  },
});
